import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import { ThemeProvider } from "@material-ui/styles";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import theme from "../../../../Themes/defaultTheme";
import { searchProfessionals } from "../../../../../Utils/apis/provisionApi";
import ProfessionalHoverPopover from "./ProfessionalHoverPopover";

/**
 * Type-ahead search-select professional picker (claude-code-UI-prompt.md §Screen structure,
 * "Professionals" — Lawyer/Valuer/Surveyor + the 4th-party Company search). Backed by
 * GET /professionals?type=&q= — server-side searched (unlike M_FirmPicker's Professional
 * Contacts equivalent, which fetches once per role and filters client-side: this repository can
 * be large and has no per-loan scoping to shrink it first), so search is debounced per keystroke.
 *
 * Not built on A_Autocomplete: that atom's search is hardwired to the CRM lookup endpoint, which
 * has nothing to do with this feature's REST contract (same reasoning as M_FirmPicker).
 */

/**
 * Module-level `${type}::${query}` -> results cache, shared by every picker instance for the
 * lifetime of the page. The repository is effectively static within a session, so re-typing or
 * re-mounting must not re-hit the API. Module scope (not a ref) deliberately: it has to survive
 * remounts, which is the case it exists for.
 */
const searchCache = new Map();
const M_ProfessionalPicker = ({
  id,
  label,
  type,
  value,
  valueLabel,
  onChange,
  required,
  error,
  helperText,
  disabled,
  fullWidth,
  placeholder,
  showContactPreview,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const requestId = useRef(0);
  // MUI's Autocomplete only calls onInputChange with reason "input" while the user is actually
  // typing — reason "reset" fires when a selection is made (to sync the displayed text with the
  // chosen option's label) and "clear" fires when the field is cleared. `inputValue` must update
  // for ALL of these (skipping "reset"/"clear" here left the box showing stale typed text after a
  // real selection, which read as "the picker doesn't let you select anything"). Search should
  // still only re-run for genuine typing, so a selection/clear is flagged to skip the next search.
  const skipNextSearch = useRef(false);

  const fetchOptions = useCallback(
    (query) => {
      const cacheKey = `${type}::${query.trim().toLowerCase()}`;
      const cached = searchCache.get(cacheKey);
      if (cached) {
        // Repeat opens/queries must not re-hit the network — a remount of this picker (from
        // anywhere up the tree) would otherwise cost another request per role.
        ++requestId.current;
        setOptions(cached);
        setLoading(false);
        setLoadError(null);
        return;
      }
      const thisRequest = ++requestId.current;
      setLoading(true);
      setLoadError(null);
      searchProfessionals(type, query)
        .then((results) => {
          const list = Array.isArray(results) ? results : [];
          searchCache.set(cacheKey, list);
          if (thisRequest === requestId.current) setOptions(list);
        })
        .catch((err) => {
          if (thisRequest === requestId.current) setLoadError(err);
        })
        .finally(() => {
          if (thisRequest === requestId.current) setLoading(false);
        });
    },
    [type]
  );

  const runSearch = useMemo(() => debounce(fetchOptions, 300), [fetchOptions]);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return undefined;
    }
    // Searching is driven by the dropdown being OPEN, never by mounting. Firing a search on mount
    // with an empty query was the root cause of the runaway
    // `professionals?type=lawyer|valuer|surveyor` traffic: three requests went out every single
    // time this section mounted or was remounted, for results nobody had asked for. Opening a
    // dropdown is a deliberate user action, and `searchCache` makes every open after the first free.
    if (!open) return undefined;
    const query = inputValue.trim();
    // Opening with nothing typed lists the whole set straight away (no minimum characters, and no
    // 300ms debounce delay on the very first list the user sees). Typing then debounces.
    if (!query) {
      fetchOptions("");
      return undefined;
    }
    runSearch(query);
    return () => runSearch.cancel();
  }, [inputValue, open, runSearch, fetchOptions]);

  // The selected option may not be in the current `options` page (e.g. on first render before
  // any search has run) — fall back to a synthetic option built from value/valueLabel so the
  // field still shows the previously-chosen professional's name.
  const selected =
    options.find((option) => option.id === value) ||
    (value ? { id: value, name: valueLabel || "" } : null);

  return (
    <ThemeProvider theme={theme}>
      {/* OQ2 — hovering the field previews the selected professional's email and contact number
          from the repository. The name the user hovers is the one already displayed in the box,
          so nothing is duplicated on screen; the preview stands down while the dropdown is open
          so it can never cover the options the user is choosing from, and while nothing is
          selected there is no-one to preview. */}
      <ProfessionalHoverPopover
        professionalId={value}
        name={selected?.name || valueLabel}
        companyName={selected?.companyName}
        disabled={!showContactPreview || !value || open}
      >
        <Autocomplete
          id={id}
          disabled={disabled}
          options={options}
          value={selected}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          filterOptions={(opts) => opts}
          noOptionsText={loading ? "Searching…" : "No matches"}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === "reset" || reason === "clear")
              skipNextSearch.current = true;
            setInputValue(newInputValue);
          }}
          getOptionLabel={(option) =>
            option?.companyName
              ? `${option.name} (${option.companyName})`
              : option?.name || ""
          }
          getOptionSelected={(option, val) => option.id === val?.id}
          onChange={(_event, newValue) => onChange(newValue)}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder || "Please type to search…"}
              variant="outlined"
              size="small"
              margin="dense"
              fullWidth={fullWidth}
              required={required}
              error={Boolean(error) || Boolean(loadError)}
              helperText={
                loadError
                  ? "Couldn't search the professionals repository"
                  : helperText
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </ProfessionalHoverPopover>
    </ThemeProvider>
  );
};

M_ProfessionalPicker.defaultProps = {
  fullWidth: true,
  required: false,
  disabled: false,
  error: false,
  helperText: "",
  showContactPreview: false,
};

M_ProfessionalPicker.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.oneOf(["lawyer", "valuer", "surveyor", "company"]).isRequired,
  value: PropTypes.string,
  valueLabel: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  placeholder: PropTypes.string,
  /** Preview the selected professional's contact details on hover (OQ2). */
  showContactPreview: PropTypes.bool,
};

// Memoized: ProfessionalsSection renders three of these side by side, and it re-renders whenever
// ANY of them resolves a name into `namesById` (or the parent touches an unrelated field). Without
// this, every such render remounts nothing but still re-runs each picker's render body — and if a
// caller ever passes an inline onChange again, this is what stops that from cascading into the
// debounced search effect. Callers MUST pass a referentially stable onChange (see
// ProfessionalsSection's useCallback-wrapped handlers) or this memo does nothing.
export default React.memo(M_ProfessionalPicker);
